import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, BookOpen, Users, Eye, ShoppingCart, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = window.location.origin;

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/analytics?range=all`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-gray-400 text-center py-8">Failed to load</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2"><TrendingUp className="w-6 h-6 text-pink-500" />Analytics</h2>
        <Button onClick={fetchData} variant="outline" className="border-gray-700 text-gray-300"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Revenue" value={`$${data.total_revenue?.toFixed(2) || 0}`} color="pink" />
        <StatCard icon={ShoppingCart} label="Sales" value={data.total_sales || 0} color="purple" />
        <StatCard icon={Eye} label="Views" value={data.total_views || 0} color="cyan" />
        <StatCard icon={Users} label="Users" value={data.total_users || 0} color="green" />
      </div>

      <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-purple-500" />Book Stats</h3>
        <table className="w-full">
          <thead><tr className="text-gray-400 text-sm border-b border-gray-700"><th className="text-left pb-2">Book</th><th className="text-right pb-2">Views</th><th className="text-right pb-2">Sales</th><th className="text-right pb-2">Revenue</th></tr></thead>
          <tbody>
            {(data.book_stats || []).map((b, i) => (
              <tr key={i} className="border-b border-gray-700/50">
                <td className="py-2 text-white text-sm" dir="rtl">{b.title}</td>
                <td className="py-2 text-right text-gray-400">{b.views}</td>
                <td className="py-2 text-right text-green-400">{b.copies_sold}</td>
                <td className="py-2 text-right text-pink-400">${b.revenue?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmallStat label="New Users" value={data.new_users || 0} />
        <SmallStat label="Active Buyers" value={data.active_buyers || 0} />
        <SmallStat label="Pending" value={data.pending_purchases || 0} />
        <SmallStat label="Conversion" value={`${(data.conversion_rate || 0).toFixed(1)}%`} />
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
    <div className={`w-9 h-9 rounded-lg bg-${color}-500/20 flex items-center justify-center mb-2`}>
      <Icon className={`w-5 h-5 text-${color}-400`} />
    </div>
    <p className="text-gray-400 text-xs">{label}</p>
    <p className="text-xl font-bold text-white">{value}</p>
  </div>
);

const SmallStat = ({ label, value }) => (
  <div className="bg-gray-900/50 rounded-lg p-3 text-center">
    <p className="text-lg font-bold text-white">{value}</p>
    <p className="text-gray-400 text-xs">{label}</p>
  </div>
);

export default AnalyticsDashboard;
